import { action } from '@ember/object';
import EmberService, { service } from '@ember/service';

import type { LineageNavigateIntent, LineageVerifyTarget, Service } from 'webapp-types';
import { VerificationObjectType, VerificationStatus } from 'webapp/enums/enums';
import { buildUnverifiedSkeleton } from 'webapp/utils/verification';

import type Metric from 'webapp/models-custom/metric';
import MetricSetup from 'webapp/models-custom/metric-setup';
import { mapSourceToDataSource } from 'webapp/models/helpers/react-model-mapper';

// Framework MetricKey::SEPARATOR + MetricKey::ATTRIBUTE_NAME — appended to the key of a custom-query /
// calculated metric that has dimensions. The lineage service drops it from `metricKey`.
const METRIC_KEY_ATTRIBUTE_SUFFIX = '|attribute';

interface LineageMetricId {
	spaceAccessId: number;
	key: string;
}

export default class LineageService extends EmberService {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('logger') declare loggerService: Service.Logger;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('verification') declare verificationService: Service.Verification;

	private logToConsole(info: string, ...params: unknown[]) {
		this.loggerService.logToConsole(`LineageService - ${info}`, ...params);
	}

	// A lineage node id for a metric is the canonical `${spaceAccessId}|${key}`.
	parseMetricId(id: string): LineageMetricId | null {
		const separatorIndex = id.indexOf('|');
		if (separatorIndex <= 0) {
			return null;
		}

		const spaceAccessId = Number(id.slice(0, separatorIndex));
		const key = id.slice(separatorIndex + 1);
		if (!key || Number.isNaN(spaceAccessId)) {
			return null;
		}

		return { spaceAccessId, key };
	}

	// The store record behind a lineage `metricKey`: the exact key or its `|attribute` form first, then
	// a space metric whose derived lineage key matches (a connector metric is stored as `Type@key`).
	// The last step also resolves the bare key of a filtered-out predefined copy.
	findMetric(metricKey: string, spaceAccessId: number): Metric | null {
		return (
			this.mmMetricsService.getMetricForKeyAndSpaceAccessId(metricKey, spaceAccessId) ??
			this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
				`${metricKey}${METRIC_KEY_ATTRIBUTE_SUFFIX}`,
				spaceAccessId,
			) ??
			this.mmMetricsService
				.getValidMetricsForSpaceAccessId(spaceAccessId)
				.find(
					(metric) =>
						this.mmMetricsService.getLineageKey(metric.key, spaceAccessId) ===
						metricKey,
				) ??
			this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
				metricKey.slice(metricKey.indexOf('|') + 1),
				spaceAccessId,
			) ??
			null
		);
	}

	@action
	async verify(target: LineageVerifyTarget): Promise<void> {
		if (target.kind === 'Metric') {
			const metricId = this.parseMetricId(target.id);
			if (!metricId) {
				this.logToConsole('verify - unusable metric id', target.id);
				return;
			}

			let metric = this.findMetric(target.id, metricId.spaceAccessId);
			if (!metric) {
				await this.mmMetricsService.requestMetrics();
				metric = this.findMetric(target.id, metricId.spaceAccessId);
			}
			if (!metric) {
				this.logToConsole('verify - metric not in store', target.id);
				return;
			}

			// The node's state is the user's intent (as for datasets below), not whatever the store holds.
			await this.mmMetricsService.setVerification(metric, target.isVerified);
			return;
		}

		const status = target.isVerified
			? VerificationStatus.VERIFIED
			: VerificationStatus.UNVERIFIED;

		await this.verificationService.toggleVerification(
			{ ...buildUnverifiedSkeleton(target.id, VerificationObjectType.DATASET), status },
			'dataset',
			target.title,
		);
	}

	@action
	navigate(kind: string, id: string, _intent?: LineageNavigateIntent) {
		if (kind === 'Dataset') {
			this.openInNewTab(
				this.navigationService.getAbsoluteUrlForRoute(
					'datamanager.connected.datasets.view',
					id,
				),
			);
			return;
		}

		if (kind === 'Metric') {
			const metricId = this.parseMetricId(id);
			if (!metricId) {
				return;
			}

			// Without the store record we can still address the metric by key alone — the drawer
			// resolves it itself — so navigation stays synchronous and popup blockers leave it alone.
			const metric = this.findMetric(id, metricId.spaceAccessId);
			this.openInNewTab(
				metric?.metricData
					? this.metricDetailsService.buildShareableUrlFromOptions({
							metricSetup: new MetricSetup(metric.metricData),
						})
					: this.metricDetailsService.buildShareableUrlFromKeyAndSpaceAccessId(
							id,
							metricId.spaceAccessId,
						),
			);
			return;
		}

		if (kind === 'DataSource') {
			const source = this.mmSourcesService.findModel(id);
			if (!source) {
				this.navigationService.transitionToRoute('datamanager.connected.index');
				return;
			}

			const dataSource = mapSourceToDataSource(
				source,
				this.currentUserService.numericId,
				this.accountService.id,
			);
			this.accountService.handleSourceDetailsRedirect(id, false, dataSource);
		}
	}

	private openInNewTab(url: string) {
		window.open(url, '_blank');
	}
}

declare module '@ember/service' {
	interface Registry {
		lineage: LineageService;
	}
}
